import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { dropTask } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';

import type FetchService from 'umg-ember-backend/services/fetch';
import type FlashMessagesService from 'umg-ember-backend/services/flash-messages';

interface AssetMapping {
  campaignName: string;
  assetHeadline: string;
  artistName: string;
}

export interface ResetVoucherResponse {
  code: string;
  campaign: string;
  maxCount: string | null;
  assetMapping: AssetMapping[];
}

interface ApiError {
  status?: number | string;
  errors?: Array<{ status?: number | string }>;
}

class ResetVoucherSettings {
  id = 0;
  isNew = false;
  recordType = 'tool-reset-voucher';

  @tracked code = '';
}

function hasStatus(error: unknown, status: number) {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const apiError = error as ApiError;

  return (
    Number(apiError.status) === status ||
    apiError.errors?.some((item) => Number(item.status) === status) === true
  );
}

export default class AdminToolResetVoucherController extends Controller {
  // Services
  @service declare fetch: FetchService;
  @service declare flashMessages: FlashMessagesService;
  @service declare headData: TODO;
  @service declare intl: IntlService;

  // State
  resetVoucherSettings = new ResetVoucherSettings();
  @tracked result: ResetVoucherResponse | null = null;

  get formattedResult() {
    return this.result ? JSON.stringify(this.result, null, 2) : null;
  }

  get isSubmitDisabled() {
    return (
      !this.resetVoucherSettings.code.trim() || this.resetVoucherTask.isRunning
    );
  }

  // Actions
  setHeadData = () => {
    this.headData.title = this.intl.t('tool_reset_voucher.headline');
    this.headData.favicon = 'tool-reset-voucher';
  };

  submit = async () => {
    if (this.isSubmitDisabled) {
      return;
    }

    const code = this.resetVoucherSettings.code.trim();
    this.result = null;

    try {
      const result = await this.resetVoucherTask.perform(code);

      if (!result) {
        return;
      }

      this.result = result;
      this.resetVoucherSettings.code = '';
      this.flashMessages.success('tool_reset_voucher.success');
    } catch (error) {
      const message = hasStatus(error, 404)
        ? 'tool_reset_voucher.error.not_found'
        : 'tool_reset_voucher.error.default';

      this.flashMessages.error(message, { sticky: true });
    }
  };

  // Tasks
  resetVoucherTask = dropTask(async (code: string) => {
    return (await this.fetch.put(`/reset_voucher/${encodeURIComponent(code)}`, {
      data: undefined,
    })) as ResetVoucherResponse;
  });
}

declare module '@ember/controller' {
  interface Registry {
    'admin.tool-reset-voucher': AdminToolResetVoucherController;
  }
}
